import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Loader from 'components/loader'
import Dialoguemodal from 'components/dialoguemodal'
import ConfirmationModal from 'components/confimation-modal'
import { currencyFormat } from 'utils'
import { dealsApi, couponsApi } from '../restaurant.api'
import { DealsProvider, useDeals } from './deals.context'
import DealForm from './deal-form'
import CouponForm from './coupon-form'

function StatusChip({ status }) {
  const active = status === 'active'
  return (
    <span
      className={`tw-text-[10px] tw-px-[7px] tw-py-[2px] tw-rounded-[3px] tw-capitalize ${active ? 'tw-bg-[#E6F0E9] tw-text-[#3F7A56]' : 'tw-bg-gray-100 tw-text-gray-500'}`}
    >
      {status}
    </span>
  )
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`tw-px-[16px] tw-py-[7px] tw-rounded-[6px] tw-text-regular ${active ? 'tw-bg-electricGreen tw-text-white' : 'tw-border tw-border-gray-300 tw-text-gray-600'}`}
    >
      {children}
    </button>
  )
}

function DealCard({ deal, onEdit, onDelete }) {
  return (
    <div className='tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-overflow-hidden tw-bg-white'>
      <div className='tw-h-32 tw-bg-[#F2ECE7]'>
        {deal.image && (
          <img
            src={deal.image}
            alt={deal.name}
            className='tw-h-full tw-w-full tw-object-cover'
          />
        )}
      </div>
      <div className='tw-p-4'>
        <div className='tw-flex tw-items-start tw-justify-between tw-gap-2'>
          <h3 className='tw-font-semibold tw-text-[15px]'>{deal.name}</h3>
          <StatusChip status={deal.status} />
        </div>
        <p className='tw-mt-1 tw-text-sm tw-text-gray-500 tw-line-clamp-2'>
          {deal.description}
        </p>
        <p className='tw-mt-2 tw-font-bold tw-text-navyBlue'>
          {currencyFormat(deal.price || 0)}{' '}
          {deal.oldPrice ? (
            <span className='tw-text-sm tw-text-gray-400 tw-line-through'>
              {currencyFormat(deal.oldPrice)}
            </span>
          ) : null}
        </p>
        <div className='tw-mt-3 tw-flex tw-gap-2'>
          <button
            type='button'
            onClick={() => onEdit(deal)}
            className='tw-text-[12px] tw-px-[10px] tw-py-[4px] tw-rounded-[5px] tw-border tw-border-gray-300 tw-text-gray-600'
          >
            Edit
          </button>
          <button
            type='button'
            onClick={() => onDelete({ type: 'deal', item: deal })}
            className='tw-text-[12px] tw-px-[10px] tw-py-[4px] tw-rounded-[5px] tw-border tw-border-[#FBE9E7] tw-text-[#C0392B]'
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

function CouponRow({ coupon, onEdit, onDelete }) {
  const discount =
    coupon.discountType === 'percentage'
      ? `${coupon.discountValue}%`
      : currencyFormat(coupon.discountValue)
  return (
    <div className='tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-3 tw-px-3 tw-rounded-[8px] tw-border tw-border-[#E7DFD9] tw-bg-white'>
      <div className='tw-overflow-hidden'>
        <p className='tw-font-mono tw-font-bold tw-text-navyBlue'>{coupon.code}</p>
        <p className='tw-text-sm tw-text-gray-500 tw-truncate'>
          {coupon.description ? `${coupon.description} · ` : ''}
          {discount} off
        </p>
      </div>
      <div className='tw-flex tw-items-center tw-gap-2 tw-shrink-0'>
        <StatusChip status={coupon.status} />
        <button
          type='button'
          onClick={() => onEdit(coupon)}
          className='tw-text-[12px] tw-px-[10px] tw-py-[4px] tw-rounded-[5px] tw-border tw-border-gray-300 tw-text-gray-600'
        >
          Edit
        </button>
        <button
          type='button'
          onClick={() => onDelete({ type: 'coupon', item: coupon })}
          className='tw-text-[12px] tw-px-[10px] tw-py-[4px] tw-rounded-[5px] tw-border tw-border-[#FBE9E7] tw-text-[#C0392B]'
        >
          Delete
        </button>
      </div>
    </div>
  )
}

function DealsInner() {
  const { loading, deals, coupons, refresh } = useDeals()
  const navigate = useNavigate()
  const [tab, setTab] = useState('deals')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null) // { type, item }

  const isDeals = tab === 'deals'

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (item) => {
    setEditing(item)
    setFormOpen(true)
  }

  const confirmDelete = async () => {
    const api = deleting.type === 'deal' ? dealsApi : couponsApi
    const { response } = await api.remove(deleting.item._id)
    if (response?.success) await refresh()
    setDeleting(null)
  }

  return (
    <div>
      <div className='tw-mb-4'>
        <button
          onClick={() => navigate('/order-management')}
          className='tw-text-[12px] tw-text-gray-400'
        >
          ← Order management
        </button>
        <h2 className='font-heading tw-text-2xl'>Deals &amp; Coupons</h2>
      </div>

      <div className='tw-mb-6 tw-flex tw-items-center tw-gap-2'>
        <TabButton active={isDeals} onClick={() => setTab('deals')}>
          Deals
        </TabButton>
        <TabButton active={!isDeals} onClick={() => setTab('coupons')}>
          Coupons
        </TabButton>
        <button
          onClick={openCreate}
          className='tw-ml-auto tw-rounded-[6px] tw-bg-electricGreen tw-text-white tw-text-regular tw-py-[8px] tw-px-[20px] tw-whitespace-nowrap'
        >
          + Add {isDeals ? 'deal' : 'coupon'}
        </button>
      </div>

      {loading ? (
        <Loader />
      ) : isDeals ? (
        deals.length ? (
          <div className='tw-grid tw-gap-4 md:tw-grid-cols-2 xl:tw-grid-cols-3'>
            {deals.map((deal) => (
              <DealCard
                key={deal._id}
                deal={deal}
                onEdit={openEdit}
                onDelete={setDeleting}
              />
            ))}
          </div>
        ) : (
          <div className='tw-text-center tw-text-gray-400 tw-py-16'>
            No deals yet
          </div>
        )
      ) : coupons.length ? (
        <div className='tw-flex tw-flex-col tw-gap-3'>
          {coupons.map((coupon) => (
            <CouponRow
              key={coupon._id}
              coupon={coupon}
              onEdit={openEdit}
              onDelete={setDeleting}
            />
          ))}
        </div>
      ) : (
        <div className='tw-text-center tw-text-gray-400 tw-py-16'>
          No coupons yet
        </div>
      )}

      <Dialoguemodal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={
          isDeals
            ? editing
              ? 'Edit deal'
              : 'Create deal'
            : editing
            ? 'Edit coupon'
            : 'Create coupon'
        }
      >
        {formOpen &&
          (isDeals ? (
            <DealForm deal={editing} onClose={() => setFormOpen(false)} />
          ) : (
            <CouponForm coupon={editing} onClose={() => setFormOpen(false)} />
          ))}
      </Dialoguemodal>

      <ConfirmationModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
      >
        <div className='tw-mb-4'>
          <h3 className='tw-text-lg tw-font-semibold tw-mb-1'>
            Delete {deleting?.type}
          </h3>
          <p className='tw-text-sm tw-text-gray-500'>
            Are you sure you want to delete “
            {deleting?.item?.name || deleting?.item?.code}”? This cannot be
            undone.
          </p>
        </div>
      </ConfirmationModal>
    </div>
  )
}

function Deals() {
  return (
    <DealsProvider>
      <DealsInner />
    </DealsProvider>
  )
}

export default Deals
